import SettingsIcon from "@mui/icons-material/Settings";
import {
  Badge,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FeatureFlagName } from "../../app/featureFlags/featureFlags";
import { canViewFeature } from "../../app/featureFlags/helpers";
import { useAppSelector } from "../../app/hooks";
import { ReactComponent as CustomersIcon } from "../../common/icons/navigation/customers.svg";
import { ReactComponent as EntriesIcon } from "../../common/icons/navigation/entries.svg";
import { ReactComponent as FeedbacksIcon } from "../../common/icons/navigation/heart.svg";
import { ReactComponent as HomeIcon } from "../../common/icons/navigation/home.svg";
import { ReactComponent as InvoicesIcon } from "../../common/icons/navigation/invoices.svg";
import { ReactComponent as JobAdIcon } from "../../common/icons/navigation/jobAds.svg";
import { ReactComponent as MessagingIcon } from "../../common/icons/navigation/messaging.svg";
import { ReactComponent as OrdersIcon } from "../../common/icons/navigation/orders.svg";
import { ReactComponent as PayslipsIcon } from "../../common/icons/navigation/payslips.svg";
import { ReactComponent as TalentsIcon } from "../../common/icons/navigation/talents.svg";
import { selectUnacknowledgedFeedbacksCount } from "../feedbacks/FeedbacksSlice";
import { selectHasUnreadMessages } from "../messaging/messagingSlice";
import { SettingsTabName } from "../settings/AdminSettings";
import { AccessRightContext } from "../user/AccessRightProvider";
import { canExpertUseMessaging } from "../user/accessRightChecks";
import NavigationListChip from "./NavigationListChip";

const NavigationList = () => {
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { isAdmin, canReadInvoices } = useContext(AccessRightContext);
  const unacknowledgedFeedbacksCount = useAppSelector(
    selectUnacknowledgedFeedbacksCount,
  );

  const hasUnreadMessages = useAppSelector(selectHasUnreadMessages);

  const { user } = useAppSelector((state) => state.user);

  const canViewMessaging =
    canViewFeature(FeatureFlagName.MESSAGING, user) &&
    canExpertUseMessaging(user);

  const navigation = useMemo(
    () => [
      {
        testId: "home-navigation-list-home-button",
        props: {
          component: Link,
          to: "/home",
          disabled: false,
        },
        title: t("home"),
        icon: <HomeIcon />,
      },
      ...(canViewMessaging
        ? [
            {
              testId: "home-navigation-list-messaging-button",
              props: {
                component: Link,
                to: "/home/messaging",
                disabled: false,
              },
              title: t("messaging:messaging"),
              icon: (
                <Badge
                  variant="dot"
                  color="warning"
                  invisible={!hasUnreadMessages}
                >
                  <MessagingIcon />
                </Badge>
              ),
            },
          ]
        : []),
      {
        testId: "home-navigation-list-talents-button",
        props: {
          component: Link,
          to: "/home/talents",
          disabled: false,
        },
        title: t("talents"),
        icon: <TalentsIcon />,
      },
      {
        testId: "home-navigation-list-customers-button",
        props: {
          component: Link,
          to: "/home/customers",
          disabled: false,
        },
        title: t("customers"),
        icon: <CustomersIcon />,
      },
      {
        testId: "home-navigation-list-orders-button",
        props: {
          component: Link,
          to: "/home/orders",
          disabled: false,
        },
        title: t("orders"),
        icon: <OrdersIcon />,
      },
      {
        testId: "home-navigation-list-entries-button",
        props: {
          component: Link,
          to: "/home/entries",
          disabled: false,
        },
        title: t("entries"),
        icon: <EntriesIcon />,
      },
      ...(canReadInvoices
        ? [
            {
              testId: "home-navigation-list-invoices-button",
              props: {
                component: Link,
                to: "/home/invoices",
                disabled: false,
              },
              title: t("invoices"),
              icon: <InvoicesIcon />,
            },
          ]
        : []),
      {
        testId: "home-navigation-list-salaries-button",
        props: {
          component: Link,
          to: "/home/salaries",
          disabled: false,
        },
        title: t("payslips"),
        icon: <PayslipsIcon />,
      },

      {
        testId: "home-navigation-list-feedbacks-button",
        props: {
          component: Link,
          to: "/home/feedbacks",
          disabled: false,
        },
        title: t("feedbacks:feedbacks"),
        icon: <FeedbacksIcon />,
        count: unacknowledgedFeedbacksCount,
      },
      {
        testId: "home-navigation-list-job-ads-button",
        props: {
          component: Link,
          to: "/home/jobAds",
          disabled: false,
        },
        title: t("jobAds"),
        icon: <JobAdIcon />,
      },
      ...(isAdmin
        ? [
            {
              testId: "home-navigation-list-settings-button",
              props: {
                component: Link,
                to: `/home/settings/${SettingsTabName.ExpenseTypesAndAccounts}`,
                disabled: false,
              },
              title: t("settings"),
              icon: <SettingsIcon sx={{ color: "white" }} />,
            },
          ]
        : []),
    ],
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAdmin,
      canReadInvoices,
      canViewMessaging,
      t,
      unacknowledgedFeedbacksCount,
      hasUnreadMessages,
    ],
  );

  useEffect(() => {
    // Set selected navigation item based on the current pathname when page is refreshed
    // Get the top level location from the pathname without ids and sub view paths
    // (Also exclude '/home', so if mainLocation if undefined, we should be on the home page)
    const mainLocation = location.pathname.replace("/home/", "").split("/")[0];
    if (mainLocation) {
      const newIndex = navigation.findIndex((x) =>
        x.props.to.includes(mainLocation),
      );
      setSelectedItemIndex(newIndex);
    } else {
      setSelectedItemIndex(0);
    }

    // remove selected item when navigating to new order page
    if (location.pathname === "/home/orders/new") {
      setSelectedItemIndex(-1);
    }
  }, [location.pathname, navigation, selectedItemIndex]);

  const handleClick = (to: string, index: number) => {
    navigate(to);
    setSelectedItemIndex(index);
  };

  return (
    <List data-testid="home-navigation-list-root">
      {navigation.map((x, index) => {
        const isSelected = index === selectedItemIndex;
        return (
          <Fragment key={x.title}>
            <ListItem
              sx={{ margin: "16px 0px", position: "relative" }}
              disablePadding
            >
              <ListItemButton
                data-testid={x.testId}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "#534482",
                    "&:hover": {
                      // add 20% transparency to 'selected' color
                      backgroundColor: "#534482CC",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                  },
                }}
                {...x.props}
                onClick={() => handleClick(x.props.to, index)}
                selected={isSelected}
              >
                <ListItemIcon sx={{ minWidth: "48px" }}>{x.icon}</ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ fontWeight: 600 }}
                  primary={x.title}
                />
                <NavigationListChip count={x?.count} />
              </ListItemButton>
            </ListItem>
            {isSelected && (
              <Divider
                key="selected-item-divider"
                sx={{
                  position: "absolute",
                  transform: `translate(5%, -8px)`,
                  width: "90%",
                  backgroundColor: "rgba(255, 255, 255, 0.85)",
                }}
              />
            )}
          </Fragment>
        );
      })}
    </List>
  );
};

export default NavigationList;
